import { getResourceCapacityForStage } from '../config/ResourceCapacityConfig';
import {
  MITOCHONDRIA_ATP_CAPACITY_BONUS,
  MITOCHONDRIA_ATP_PER_SECOND,
  MITOCHONDRIA_GLUCOSE_PER_SECOND,
  MOVEMENT_ATP_COST_PER_SECOND,
  MOVING_REGEN_MULTIPLIER,
  NATURAL_ATP_REGEN_PER_SECOND
} from './GameConstants';

interface ATPRegenerationInput {
  isMoving: boolean;
  elapsedSeconds: number;
  currentATP: number;
  maxATP: number;
}

interface MitochondriaProductionInput {
  mitochondriaCount: number;
  elapsedSeconds: number;
  glucose: number;
  currentATP: number;
  maxATP: number;
  glucosePerMitoPerSecond?: number;
  atpPerMitoPerSecond?: number;
}

export interface MitochondriaProductionResult {
  atpProduced: number;
  glucoseConsumed: number;
}

export function calculateMovementATPCost(elapsedSeconds: number): number {
  return MOVEMENT_ATP_COST_PER_SECOND * Math.max(elapsedSeconds, 0);
}

export function calculateATPRegeneration(input: ATPRegenerationInput): number {
  const { isMoving, elapsedSeconds, currentATP, maxATP } = input;
  if (currentATP >= maxATP || elapsedSeconds <= 0) {
    return 0;
  }

  const regenerationRate = isMoving
    ? NATURAL_ATP_REGEN_PER_SECOND * MOVING_REGEN_MULTIPLIER
    : NATURAL_ATP_REGEN_PER_SECOND;
  const rawGain = regenerationRate * elapsedSeconds;
  return Math.min(rawGain, maxATP - currentATP);
}

export function calculateMitochondriaProduction(
  input: MitochondriaProductionInput
): MitochondriaProductionResult {
  const {
    mitochondriaCount,
    elapsedSeconds,
    glucose,
    currentATP,
    maxATP,
    glucosePerMitoPerSecond = MITOCHONDRIA_GLUCOSE_PER_SECOND,
    atpPerMitoPerSecond = MITOCHONDRIA_ATP_PER_SECOND
  } = input;

  if (
    mitochondriaCount <= 0 ||
    elapsedSeconds <= 0 ||
    glucose <= 0 ||
    currentATP >= maxATP
  ) {
    return { atpProduced: 0, glucoseConsumed: 0 };
  }

  const glucoseToConsume = mitochondriaCount * glucosePerMitoPerSecond * elapsedSeconds;
  if (glucoseToConsume <= 0) {
    return { atpProduced: 0, glucoseConsumed: 0 };
  }

  const actualGlucoseConsumed = Math.min(glucoseToConsume, glucose);
  const rawATPProduction = mitochondriaCount * atpPerMitoPerSecond * elapsedSeconds;
  const atpProductionRatio = actualGlucoseConsumed / glucoseToConsume;
  const actualATPProduced = Math.min(
    rawATPProduction * atpProductionRatio,
    maxATP - currentATP
  );

  return {
    atpProduced: actualATPProduced,
    glucoseConsumed: actualGlucoseConsumed
  };
}

export function calculateMaxATP(
  membraneStage: number,
  mitochondriaCount: number,
  mitochondriaCapacityBonus: number = MITOCHONDRIA_ATP_CAPACITY_BONUS
): number {
  const baseCapacity = getResourceCapacityForStage(membraneStage).maxATP;
  return baseCapacity + Math.max(0, mitochondriaCount) * mitochondriaCapacityBonus;
}
